// src/services/groupAdminApi.js (New RTK Query Service File)

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from '../store/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: 'http://localhost:5000/api/group-admin', // Base URL changed for Group Admin routes
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('x-auth-token', token);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && [401, 403].includes(result.error.status)) {
    api.dispatch(logout());
  }
  return result;
};


export const groupAdminApi = createApi({
  reducerPath: 'groupAdminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ManagedUsers', 'ManagedDevices', 'GroupEvents'], 
  endpoints: (builder) => ({
    
    // ---------------------- 1. Group Data Queries ----------------------
    getManagedUsers: builder.query({
      query: () => '/my-managed-users',
      providesTags: ['ManagedUsers'], 
    }),

    getManagedUsersDevices: builder.query({
      query: () => '/my-managed-users-devices',
      providesTags: ['ManagedDevices'],
    }),

    getGroupEvents: builder.query({
      query: () => '/events', // Assuming the route is /group-admin/events
      providesTags: ['GroupEvents'],
    }),
    
    // ---------------------- 2. User Mutations (Managed Users) ----------------------
    
    createManagedUser: builder.mutation({
      query: (userData) => ({
        url: '/users',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['ManagedUsers', 'ManagedDevices'], // User creation/assignment affects both lists
    }),
    
    updateManagedUser: builder.mutation({
      query: ({ userId, ...patch }) => ({
        url: `/managed-users/${userId}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ['ManagedUsers'], 
    }),

    deleteManagedUser: builder.mutation({
      query: (userId) => ({
        url: `/managed-users/${userId}`, // This is actually unassigning/removing
        method: 'DELETE',
      }),
      invalidatesTags: ['ManagedUsers', 'ManagedDevices'], // Unassigning affects both lists
    }),
    
    // ---------------------- 3. Device Mutations (Managed Devices) ----------------------
    
    addManagedDevice: builder.mutation({
      query: (deviceData) => ({
        url: '/devices',
        method: 'POST',
        body: deviceData,
      }),
      invalidatesTags: ['ManagedDevices'], 
    }),
    
    updateManagedDevice: builder.mutation({
      query: ({ deviceId, ...patch }) => ({
        url: `/devices/${deviceId}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ['ManagedDevices'], 
    }),
    
    deleteManagedDevice: builder.mutation({
      query: (deviceId) => ({
        url: `/devices/${deviceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ManagedDevices'],
    }),

    assignDeviceToManagedUser: builder.mutation({
      query: ({ deviceId, userId }) => ({
        url: `/devices/${deviceId}/assign`,
        method: 'PUT',
        body: { userId },
      }),
      invalidatesTags: ['ManagedDevices'],
    }),

    // Send Command (Example)
    sendCommandToManagedDevice: builder.mutation({
        query: ({ deviceId, command }) => ({
            url: `/devices/${deviceId}/command`,
            method: 'POST',
            body: { command },
        }),
        // Does not typically invalidate tags, but might update device status eventually
        invalidatesTags: (result, error, { deviceId }) => [{ type: 'Device', id: deviceId }],
    }),
    
  }),
});

// Export Hooks
export const {
  useGetManagedUsersQuery,
  useGetManagedUsersDevicesQuery,
  useGetGroupEventsQuery,
  useCreateManagedUserMutation,
  useUpdateManagedUserMutation,
  useDeleteManagedUserMutation,
  useAddManagedDeviceMutation,
  useUpdateManagedDeviceMutation,
  useDeleteManagedDeviceMutation,
  useAssignDeviceToManagedUserMutation,
  useSendCommandToManagedDeviceMutation,
} = groupAdminApi;